import React, { useMemo, useRef, useEffect, useState } from "react";
import useFetchArrivals from "../hooks/useFetchArrivals";

const TRANSFER_STOP_IDS = { E: "G14", F: "G14", "7": "710" };
const TRANSFER_ROUTES = ["E", "F", "7"];

function ArrivalBoard({ systemId, stopId }) {
  const { data, loading, error } = useFetchArrivals(systemId, stopId, 10000);
  const arrivals = (data?.stopTimes || []).filter(
    a => a.trip.directionId === true && (a.trip.route.id === "M" || a.trip.route.id === "R")
  );

  // Fetch transfer arrivals
  const arrivalsG14 = useFetchArrivals("us-ny-subway", "G14");
  const arrivals710 = useFetchArrivals("us-ny-subway", "710");

  // For each M/R arrival, compute transfer waits and get route color
  const transferWaits = useMemo(() => {
    if (!arrivalsG14.data?.stopTimes && !arrivals710.data?.stopTimes) return {};
    const waits = {};
    arrivals.forEach(arrival => {
      let transferArrivalTime = null;
      if (arrivalsG14.data?.stopTimes) {
        const match = arrivalsG14.data.stopTimes.find(
          s => s.trip.id === arrival.trip.id && s.trip.route.id === arrival.trip.route.id
        );
        if (match) transferArrivalTime = parseInt(match.arrival.time);
      }
      if (!transferArrivalTime) return;
      waits[arrival.trip.id] = {};
      for (const routeId of TRANSFER_ROUTES) {
        let transferArrivals = [];
        if (routeId === "7") {
          transferArrivals = (arrivals710.data?.stopTimes || []).filter(
            a => a.trip.route.id === "7" && a.trip.directionId === false
          );
        } else {
          transferArrivals = (arrivalsG14.data?.stopTimes || []).filter(
            a => a.trip.route.id === routeId && a.trip.directionId === true
          );
        }
        const next = transferArrivals.find(
          a => parseInt(a.arrival.time) >= transferArrivalTime
        );
        if (next) {
          waits[arrival.trip.id][routeId] = {
            wait: Math.round((parseInt(next.arrival.time) - transferArrivalTime) / 60),
            color: next.trip.route.color,
          };
        }
      }
    });
    return waits;
  }, [arrivals, arrivalsG14.data, arrivals710.data]);

  // --- BUS ARRIVALS ---
  const BUS_STOP_ID = "502992";
  const BUS_API_KEY = "bb46e5ab-707a-4178-8f8b-388e766ef44b"; // User's actual API key
  const [busArrivals, setBusArrivals] = useState([]);
  const [busError, setBusError] = useState(null);

  // Auto-refresh bus arrivals every 10 seconds
  useEffect(() => {
    let cancelled = false;
    function fetchBus() {
      setBusError(null);
      fetch(`https://corsproxy.io/?https://bustime.mta.info/api/siri/stop-monitoring.json?key=${BUS_API_KEY}&MonitoringRef=${BUS_STOP_ID}`, {
        headers: {
          'Accept': 'application/json'
        }
      })
        .then(res => res.json())
        .then(data => {
          if (!cancelled) {
            const visits = data?.Siri?.ServiceDelivery?.StopMonitoringDelivery?.[0]?.MonitoredStopVisit || [];
            setBusArrivals(visits);
          }
        })
        .catch(e => {
          if (!cancelled) setBusError(e.message);
        });
    }
    fetchBus();
    const interval = setInterval(fetchBus, 10000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // Auto-refresh subway arrivals every 10 seconds (by forcing re-render)
  const [refreshTick, setRefreshTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setRefreshTick(t => t + 1), 10000);
    return () => clearInterval(interval);
  }, []);

  const arrivalListRef = useRef(null);
  const scrollPosRef = useRef(0);

  // Save scroll position before loading
  useEffect(() => {
    if (loading && arrivalListRef.current) {
      scrollPosRef.current = arrivalListRef.current.scrollTop;
    }
  }, [loading]);

  // Restore scroll position after loading
  useEffect(() => {
    if (!loading && arrivalListRef.current) {
      arrivalListRef.current.scrollTop = scrollPosRef.current;
    }
  }, [loading, arrivals.length]);

  function renderRouteBadge(route, colorOverride, small = false) {
    const color = colorOverride || (route?.color ? `#${route.color}` : "#333");
    return (
      <span
        className="route-badge"
        style={{
          background: color,
          minWidth: small ? 44 : 90,
          height: small ? 44 : 90,
          lineHeight: small ? "44px" : "90px",
          fontSize: small ? '2rem' : '3.2rem',
          marginRight: small ? 8 : 40,
          boxShadow: small ? "none" : undefined,
        }}
      >
        {route?.id || route?.name || "?"}
      </span>
    );
  }

  // Prepare a unified, time-sorted list of arrivals (subway + bus)
  const now = Date.now();
  const isWeekday = (() => {
    const day = new Date().getDay();
    return day >= 1 && day <= 5; // 1=Monday, 5=Friday
  })();

  const subwayArrivals = arrivals.map(arrival => {
    const secondsToLeave = parseInt(arrival.departure.time) - Math.floor(now / 1000);
    return {
      type: 'subway',
      key: `subway-${arrival.trip.route.id}-${arrival.departure.time}`,
      route: arrival.trip.route,
      time: secondsToLeave,
      waits: transferWaits[arrival.trip.id] || {},
      arrival,
    };
  });
  const busArrivalsUnified = busArrivals
    .filter(visit => {
      let route = visit.MonitoredVehicleJourney?.LineRef || "";
      if (route.startsWith('MTA NYCT_')) route = route.replace('MTA NYCT_', '');
      if (route === 'Q59') return false;
      const expected = visit.MonitoredVehicleJourney?.MonitoredCall?.ExpectedArrivalTime;
      return !!expected;
    })
    .map((visit, idx) => {
      const mvj = visit.MonitoredVehicleJourney;
      let route = mvj?.LineRef || "?";
      if (route.startsWith('MTA NYCT_')) route = route.replace('MTA NYCT_', '');
      const dest = mvj?.DestinationName || "";
      const expected = mvj?.MonitoredCall?.ExpectedArrivalTime;
      let min = null;
      let time = null;
      if (expected) {
        time = Math.floor((new Date(expected).getTime() - now) / 1000);
        min = time > 60 ? `${Math.floor(time / 60)} min` : (time > 0 ? `${time} sec` : "Due");
      }
      // Use VehicleRef and expected time for unique key if available
      const vehicleRef = mvj?.VehicleRef || '';
      return {
        type: 'bus',
        key: `bus-${route}-${expected}-${vehicleRef}`,
        route: { id: route },
        time,
        min,
        dest,
        routeRaw: route,
        expected,
        vehicleRef,
      };
    })
    // Remove duplicates: keep only the first occurrence of each route+expected+dest
    .filter((item, idx, arr) =>
      arr.findIndex(
        x => x.routeRaw === item.routeRaw && x.expected === item.expected && x.dest === item.dest && x.vehicleRef === item.vehicleRef
      ) === idx
    );

  let unifiedArrivals;
  if (isWeekday) {
    // Sort subway and bus separately
    const sortedSubway = subwayArrivals.filter(item => item.time !== null && item.time >= 0).sort((a, b) => a.time - b.time);
    const sortedBus = busArrivalsUnified.filter(item => item.time !== null && item.time >= 0).sort((a, b) => a.time - b.time);
    // Take first two subway, then the rest (subway+bus) sorted by time
    const firstTwoSubway = sortedSubway.slice(0, 2);
    const rest = [...sortedSubway.slice(2), ...sortedBus].sort((a, b) => a.time - b.time);
    unifiedArrivals = [...firstTwoSubway, ...rest];
  } else {
    // Weekend: just sort all by time
    unifiedArrivals = [...subwayArrivals, ...busArrivalsUnified].filter(item => item.time !== null && item.time >= 0).sort((a, b) => a.time - b.time);
  }

  return (
    <div>
      {error && <p style={{ color: "red", textAlign: "center" }}>Error: {error}</p>}
      {!loading && arrivals.length === 0 && (
        <p style={{ textAlign: "center", color: "#888" }}>No upcoming arrivals.</p>
      )}
      <ul className="arrival-list" ref={arrivalListRef}>
        {unifiedArrivals.map(item => {
          if (item.type === 'subway') {
            const { route, time, waits, arrival } = item;
            return (
              <li key={item.key} className="arrival-item">
                {renderRouteBadge(route)}
                <div className="arrival-details">
                  <div className="arrival-time">
                    {time > 60 ? `${Math.floor(time / 60)} min` : `${time > 0 ? time : 0} sec`}
                  </div>
                  <div className="transfer-badges-row" style={{ marginTop: 4 }}>
                    {TRANSFER_ROUTES.map(rid =>
                      waits[rid] !== undefined ? (
                        <span key={rid} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                          {renderRouteBadge({ id: rid }, waits[rid].color ? `#${waits[rid].color}` : undefined, true)}
                          <span style={{ fontSize: '1.2rem', color: "#888", margin: "0 2px", fontWeight: 500 }}>
                            {rid === "7" ? "to Flushing" : "to Manhattan"}
                          </span>
                          <span style={{ minWidth: 20, fontSize: '1.2rem', fontWeight: 600, color: "#1a7f37" }}>{waits[rid].wait}m</span>
                        </span>
                      ) : null
                    )}
                  </div>
                </div>
              </li>
            );
          } else if (item.type === 'bus') {
            const { route, min, dest, key } = item;
            // Remove leading 'Q' from bus route name
            let displayRoute = { ...route };
            if (displayRoute.id && displayRoute.id.startsWith('Q')) {
              displayRoute = { ...displayRoute, id: displayRoute.id.slice(1) };
            }
            return (
              <li key={key} className="arrival-item">
                {renderRouteBadge(displayRoute, '#0055a4')}
                <div className="arrival-details">
                  <div className="arrival-time">{min || "-"}</div>
                  {dest && (
                    <div className="transfer-badges-row" style={{ marginTop: 4 }}>
                      <span style={{ fontSize: '1.2rem', color: '#888', margin: '0 2px', fontWeight: 500 }}>{dest}</span>
                    </div>
                  )}
                </div>
              </li>
            );
          }
          return null;
        })}
        {busError && <li className="arrival-item" style={{ color: 'red' }}>Bus error: {busError}</li>}
      </ul>
    </div>
  );
}

export default ArrivalBoard; 